import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render errors anywhere in the descendant tree and shows a
 * recovery UI instead of blanking the entire app.
 *
 * - Logs the error to console (visible to devs, not users).
 * - Shows a generic Italian fallback with a reload button.
 * - Never exposes the raw error message to end users.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="flex flex-col items-center justify-center min-h-screen p-6 bg-slate-900 text-slate-100"
        >
          <h1 className="text-2xl font-semibold mb-3">Qualcosa è andato storto</h1>
          <p className="text-slate-300 mb-6 text-center max-w-md">
            Si è verificato un errore inaspettato. Ricarica la pagina per continuare.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors"
          >
            Ricarica la pagina
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
